import type { Node, Edge } from "@xyflow/react";
import { NodeData } from "../types/types";

export interface AIContextPayload {
  conversationHistory: Array<{ role: string; content: string }>;
  selectedNode?: {
    id: string;
    label: string;
    notes: string;
    level: number;
  };
  nearbyNodes?: Array<{
    id: string;
    label: string;
    relationship: "parent" | "sibling" | "child";
  }>;
  mapSummary?: {
    totalNodes: number;
    rootLabel: string;
    maxDepth: number;
  };
  fullStructure?: {
    nodes: Array<{ id: string; label: string; parentId?: string }>;
    edges: Array<{ source: string; target: string }>;
  };
}

function calculateMaxDepth(nodes: Node<NodeData>[], edges: Edge[]): number {
  const getDepth = (nodeId: string, visited = new Set<string>()): number => {
    if (visited.has(nodeId)) return 0;
    visited.add(nodeId);

    const children = edges.filter((e) => e.source === nodeId);
    if (children.length === 0) return 1;

    return 1 + Math.max(...children.map((e) => getDepth(e.target, visited)));
  };

  const rootNode = nodes.find((n) => n.type === "center");
  return rootNode ? getDepth(rootNode.id) : 0;
}

function getNodeLevel(
  nodeId: string,
  nodes: Node<NodeData>[],
  edges: Edge[],
): number {
  let level = 0;
  let currentId = nodeId;

  while (true) {
    const parentEdge = edges.find((e) => e.target === currentId);
    if (!parentEdge) break;
    level++;
    currentId = parentEdge.source;
  }

  return level;
}

function getNearbyNodes(
  nodeId: string,
  nodes: Node<NodeData>[],
  edges: Edge[],
): Array<{
  id: string;
  label: string;
  relationship: "parent" | "sibling" | "child";
}> {
  const nearby: Array<{
    id: string;
    label: string;
    relationship: "parent" | "sibling" | "child";
  }> = [];

  // Find parent
  const parentEdge = edges.find((e) => e.target === nodeId);
  if (parentEdge) {
    const parent = nodes.find((n) => n.id === parentEdge.source);
    if (parent) {
      nearby.push({
        id: parent.id,
        label: parent.data.label,
        relationship: "parent",
      });
    }

    // Find siblings (same parent)
    const siblings = edges
      .filter((e) => e.source === parentEdge.source && e.target !== nodeId)
      .map((e) => nodes.find((n) => n.id === e.target))
      .filter((n) => n !== undefined)
      .map((n) => ({
        id: n!.id,
        label: n!.data.label,
        relationship: "sibling" as const,
      }));
    nearby.push(...siblings);
  }

  // Find children
  const children = edges
    .filter((e) => e.source === nodeId)
    .map((e) => nodes.find((n) => n.id === e.target))
    .filter((n) => n !== undefined)
    .map((n) => ({
      id: n!.id,
      label: n!.data.label,
      relationship: "child" as const,
    }));
  nearby.push(...children);

  return nearby;
}

export function buildAIContext(
  nodes: Node<NodeData>[],
  edges: Edge[],
  selectedNodeId: string | null,
  chatHistory: Array<{ role: string; content: string }>,
): AIContextPayload {
  const context: AIContextPayload = {
    conversationHistory: chatHistory,
  };

  // Map summary (always include - lightweight)
  const rootNode = nodes.find((n) => n.type === "center");
  context.mapSummary = {
    totalNodes: nodes.length,
    rootLabel: rootNode?.data.label || "Untitled",
    maxDepth: calculateMaxDepth(nodes, edges),
  };

  // Selected node context (if any)
  if (selectedNodeId) {
    const selectedNode = nodes.find((n) => n.id === selectedNodeId);
    if (selectedNode) {
      const level = getNodeLevel(selectedNode.id, nodes, edges);

      context.selectedNode = {
        id: selectedNode.id,
        label: selectedNode.data.label,
        notes: selectedNode.data.notes || "",
        level,
      };

      // Get nearby nodes for context
      context.nearbyNodes = getNearbyNodes(selectedNode.id, nodes, edges);
    }
  }

  // Full structure only for small maps (< 20 nodes)
  if (nodes.length <= 20) {
    context.fullStructure = {
      nodes: nodes.map((n) => ({
        id: n.id,
        label: n.data.label,
        parentId: n.parentId,
      })),
      edges: edges.map((e) => ({
        source: e.source,
        target: e.target,
      })),
    };
  }

  return context;
}

export function buildSystemPromptWithContext(
  nodes: Node<NodeData>[],
  edges: Edge[],
  selectedNodeId: string | null,
  chatHistory: Array<{ role: string; content: string }>,
): string {
  const context = buildAIContext(nodes, edges, selectedNodeId, chatHistory);

  let prompt = `You are an AI assistant for FocusMap, helping users build mind maps through conversation.

## OUTPUT FORMAT

🎯 **SIMPLE TEXT OUTPUT - NO JSON REQUIRED**

Respond with a brief conversational message followed by a structured outline using markdown formatting:

**Format:**
1. Conversational response (1-2 sentences)
2. Blank line
3. Structured outline using bullets or headers

**Supported formats:**
- Bullet lists with \`-\` or \`*\` and indentation (2 spaces per level)
- Numbered lists with \`1.\` \`2.\` etc.
- Markdown headers \`#\` \`##\` \`###\`

**Example response:**
\`\`\`
I'll create a marketing plan structure for you.

# Marketing Plan
- Strategy
  - Target Audience
  - Value Proposition
- Execution
  - Content Calendar
  - Social Media
- Metrics
  - KPIs
  - ROI Analysis
\`\`\`

**For detailed content:** Use short labels in the structure, add details as sub-points:
\`\`\`
Creating your project structure.

# Project Alpha
- Overview
  - Project goals and objectives
  - Timeline: Q1 2026
  - Budget: $50K
- Team
  - John - Project Lead
  - Sarah - Designer
  - Mike - Developer
\`\`\`

✅ **This format works for:**
- Your AI-generated responses
- Copy/paste from ChatGPT, Claude, Gemini
- Any structured text or outline

You are an AI assistant for FocusMap, helping users build mind maps through conversation.

## CURRENT MAP CONTEXT

Map Summary:
- Total nodes: ${context.mapSummary?.totalNodes || 0}
- Root goal: "${context.mapSummary?.rootLabel || "None"}"
- Max depth: ${context.mapSummary?.maxDepth || 0} levels

`;

  if (context.selectedNode) {
    prompt += `Selected Node Context:
- Label: "${context.selectedNode.label}"
- Level: ${context.selectedNode.level}
- Notes: ${
      context.selectedNode.notes
        ? context.selectedNode.notes.substring(0, 200)
        : "None"
    }

`;

    if (context.nearbyNodes && context.nearbyNodes.length > 0) {
      prompt += `Nearby Nodes:\n`;
      context.nearbyNodes.forEach((n) => {
        prompt += `- ${n.relationship}: "${n.label}"\n`;
      });
      prompt += `\n`;
    }
  }

  if (context.fullStructure) {
    prompt += `Full Map Structure:\n`;
    context.fullStructure.nodes.forEach((n) => {
      prompt += `- "${n.label}" (ID: ${n.id})${
        n.parentId ? ` under ${n.parentId}` : ""
      }\n`;
    });
    prompt += `\n`;
  }

  prompt += `## GENERATION RULES

1. **Keep labels SHORT** (2-5 words max)
2. **Add descriptive text**: After each structure line, add 1-3 lines of explanatory text (this becomes the node's notes)
3. **When selected node exists**: Create structure as children of selected node
4. **When no selection**: Add to root/center node
5. **For hierarchical content**: Use proper indentation (2 spaces per level)
6. **Respond in Danish** when user speaks Danish
7. **PRESERVE NUMBERS**: If user input has numbered items (1., 2., 3.), keep the numbers in your output

## OUTPUT FORMATS

Structure lines:
- Bullet lists: \`- Item\` or \`* Item\`
- Numbered lists: \`1. Item\`, \`2. Item\` (PRESERVE numbers from user input)
- Headers: \`# Main\`, \`## Sub\`
- Indentation: 2 spaces per level

After each structure line, add descriptive text:
- This text will automatically become the node's notes
- Use 1-3 lines of explanation
- Leave blank line before next structure item

## EXAMPLES

**Example 1 - Simple list with descriptions:**
\`\`\`
Creating marketing channels for you.

- Social Media
Connect with audiences on Facebook, Instagram, LinkedIn and Twitter.

- Email Marketing
Build subscriber lists and send targeted campaigns with newsletters.

- Content Marketing  
Create valuable blog posts, videos and guides to attract customers.

- Paid Advertising
Run targeted ads on Google, Facebook and LinkedIn to drive traffic.
\`\`\`

**Example 2 - Hierarchical structure with descriptions:**
\`\`\`
I'll structure your project plan.

# Q1 Goals
Main objectives for the first quarter of the year.

- Product Launch
Release the new product to market with full feature set.
  - MVP Development
  Build core features and test with beta users.
  - Beta Testing
  Gather feedback and fix critical issues before launch.
  
- Marketing Campaign
Create awareness and generate leads for the new product.
  - Brand Identity
  Design logo, colors and brand guidelines.
  - Launch Event
  Plan and execute virtual launch event with demos.
\`\`\`

**Example 3 - Numbered list with descriptions (PRESERVE NUMBERS):**
\`\`\`
Strukturerer dine killer features.

1. Auto-Structure AI
En AI der automatisk omsætter en idé til en struktureret map med mål, deadlines og KPI'er.

2. Multi-View Consistency Engine
At alt hænger sammen på tværs af views - Task optræder i Timeline og Kanban.

3. Structured Collaboration Layer
Roller, versioner, forslag og accept for delte projekter med revisionsspor.
\`\`\`

**Example 4 - Mixed sections with details:**
\`\`\`
Here are your current and potential features.

# Current Killer Features
Features that already differentiate you from competitors.

1. Idea to action - in one map
Connect ideas, structure, goals, and tasks in one visual universe.

2. Multi-view navigation
Seamless navigation between Map, Timeline and Kanban with 100% data consistency.

3. Structural simplicity
Always maintain overview while working with complex projects.

# Potential Features
Features that could take you to the next level.

1. Auto-Structure AI
AI that automatically creates complete project structures from descriptions.

2. Integration Engine
Connect with existing company systems to lower adoption barriers.
\`\`\`

Start responding to the user!
`;

  return prompt;
}
